import { FC, useMemo } from 'react'

import { filterSuggestionItems } from '@blocknote/core'
import { SuggestionMenuController, SuggestionMenuProps } from '@blocknote/react'

import { QuoteVariableType } from '@sequencehq/quote-content'

import { CustomSuggestionMenu } from '../CustomSuggestionMenu/CustomSuggestionMenu'
import { blocknoteSchema } from '../blocknoteSchema'
import { getVariableLabel } from '../../utils/quoteVariables'
import { SuggestionItem } from '../CustomSuggestionMenu/types'
import { MonoSpaced12SemiBold } from '@sequencehq/core-components'

const QUOTE_VARIABLE_TRIGGER_CHARACTERS = ['@', '{']

const createMenu =
  (params: {
    editor: typeof blocknoteSchema.BlockNoteEditor
    addPhaseBlockEnabled: boolean
  }) =>
  (props: SuggestionMenuProps<SuggestionItem>) => {
    return (
      <CustomSuggestionMenu
        {...props}
        editor={params.editor}
        addPhaseBlockEnabled={params.addPhaseBlockEnabled}
        data-testid="variables"
      />
    )
  }

export const QuoteVariablesSuggestionController: FC<{
  editor: typeof blocknoteSchema.BlockNoteEditor
  addPhaseBlockEnabled: boolean
  quoteVariablesValues: Record<QuoteVariableType, string | undefined>
}> = ({ editor, quoteVariablesValues, addPhaseBlockEnabled }) => {
  const Menu = useMemo(() => {
    return createMenu({
      editor: editor,
      addPhaseBlockEnabled
    })
  }, [editor, addPhaseBlockEnabled])

  const getItems = (query: string) =>
    Promise.resolve(
      filterSuggestionItems(
        getVariablesMenuItems(editor, quoteVariablesValues),
        query
      )
    )

  /**
   * Blocknote's `triggerCharacter` only accepts a single exact-match string
   * (no array or regex), so we register one controller per trigger. All open
   * the same quote-variables menu — `{` mirrors how variables render (wrapped
   * in curly braces).
   */
  return (
    <>
      {QUOTE_VARIABLE_TRIGGER_CHARACTERS.map(triggerCharacter => (
        <SuggestionMenuController
          key={triggerCharacter}
          triggerCharacter={triggerCharacter}
          getItems={getItems}
          suggestionMenuComponent={Menu}
        />
      ))}
    </>
  )
}

const variableMenuItem =
  (
    editor: typeof blocknoteSchema.BlockNoteEditor,
    quoteVariablesValues: Record<QuoteVariableType, string | undefined>
  ) =>
  (variableType: QuoteVariableType): SuggestionItem => ({
    title: `{${getVariableLabel(variableType)}}`,
    onItemClick: () => {
      editor.insertInlineContent([
        {
          type: 'variable',
          props: { variableType },
          content: [
            {
              type: 'text',
              text:
                quoteVariablesValues[variableType] ??
                getVariableLabel(variableType),
              styles: {}
            }
          ]
        },
        ' '
      ])
    },
    font: MonoSpaced12SemiBold
  })

export const getVariablesMenuItems = (
  editor: typeof blocknoteSchema.BlockNoteEditor,
  quoteVariablesValues: Record<QuoteVariableType, string | undefined>
): SuggestionItem[] => {
  const getVariableMenuItem = variableMenuItem(editor, quoteVariablesValues)

  return [
    getVariableMenuItem('CUSTOMER_NAME'),
    getVariableMenuItem('CUSTOMER_ADDRESS'),
    getVariableMenuItem('CUSTOMER_COUNTRY'),
    getVariableMenuItem('RECIPIENTS'),
    getVariableMenuItem('QUOTE_OWNER'),
    getVariableMenuItem('START_DATE'),
    getVariableMenuItem('END_DATE'),
    getVariableMenuItem('DURATION'),
    getVariableMenuItem('PAYMENT_TERMS'),
    getVariableMenuItem('CURRENCY')
  ]
}
